/**
 * 浏览器兼容性检测和修复工具
 * 支持Chrome, Edge, Firefox, Safari, 360极速浏览器等主流浏览器
 */

/**
 * 检测浏览器类型和版本
 */
export function detectBrowser() {
  const ua = navigator.userAgent;
  let browserName = 'Unknown';
  let version = 'Unknown';
  
  // 360极速浏览器（基于Chromium内核）
  // 必须在Chrome检测之前，因为360浏览器的UA也包含Chrome
  if (ua.indexOf('360SE') > -1 || ua.indexOf('360EE') > -1 || ua.indexOf('QIHU 360') > -1) {
    browserName = '360极速浏览器';
    // 360浏览器通常不暴露自己的版本号，使用Chrome版本作为参考
    version = ua.match(/Chrome\/(\d+)/)?.[1] || 'Unknown';
  }
  // Edge（含 iOS 的 EdgiOS）
  else if (ua.indexOf('Edg/') > -1 || ua.indexOf('EdgiOS') > -1) {
    browserName = 'Edge';
    version = ua.match(/(?:Edg|EdgiOS)\/(\d+)/)?.[1] || 'Unknown';
  }
  // Chrome（含 iOS 的 CriOS）
  else if ((ua.indexOf('Chrome') > -1 || ua.indexOf('CriOS') > -1) && ua.indexOf('Edg/') === -1 && ua.indexOf('EdgiOS') === -1) {
    browserName = 'Chrome';
    version = ua.match(/(?:Chrome|CriOS)\/(\d+)/)?.[1] || 'Unknown';
  }
  // Firefox（含 iOS 的 FxiOS）
  else if (ua.indexOf('Firefox') > -1 || ua.indexOf('FxiOS') > -1) {
    browserName = 'Firefox';
    version = ua.match(/(?:Firefox|FxiOS)\/(\d+)/)?.[1] || 'Unknown';
  }
  // Safari
  else if (ua.indexOf('Safari') > -1 && ua.indexOf('Chrome') === -1) {
    browserName = 'Safari';
    version = ua.match(/Version\/(\d+)/)?.[1] || 'Unknown';
  }
  
  return { browserName, version: parseInt(version) };
}

/**
 * 检查浏览器是否支持所需的音频API
 */
export function checkAudioAPISupport() {
  const support = {
    audioContext: !!(window.AudioContext || window.webkitAudioContext),
    getUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
    audioWorklet: false,
    scriptProcessor: false,
    mediaRecorder: !!(window.MediaRecorder),
    webSocket: !!(window.WebSocket),
    speechRecognition: !!(window.SpeechRecognition || window.webkitSpeechRecognition)
  };
  
  // 检查AudioWorklet支持（需要先创建AudioContext）
  try {
    const tempContext = new (window.AudioContext || window.webkitAudioContext)();
    support.audioWorklet = !!(tempContext.audioWorklet);
    support.scriptProcessor = !!(tempContext.createScriptProcessor);
    tempContext.close();
  } catch (error) {
  }
  
  return support;
}

/**
 * 获取AudioContext构造函数（兼容性处理）
 */
export function getAudioContext() {
  return window.AudioContext || window.webkitAudioContext;
}

/**
 * 创建AudioContext实例（兼容性处理）
 */
export function createAudioContext(options = {}) {
  const AudioContextClass = getAudioContext();
  if (!AudioContextClass) {
    throw new Error('浏览器不支持AudioContext');
  }
  
  try {
    return new AudioContextClass(options);
  } catch (error) {
    return new AudioContextClass();
  }
}

/**
 * 恢复AudioContext（处理浏览器的自动挂起策略）
 */
export async function resumeAudioContext(audioContext) {
  if (!audioContext) return;
  
  try {
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }
  } catch (error) {
  }
}

/**
 * 获取用户媒体（兼容性处理）
 */
export async function getUserMedia(constraints) {
  // 标准API
  if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
    return await navigator.mediaDevices.getUserMedia(constraints);
  }
  
  // 旧版API回退
  const legacyGetUserMedia = 
    navigator.getUserMedia || 
    navigator.webkitGetUserMedia || 
    navigator.mozGetUserMedia || 
    navigator.msGetUserMedia;
  
  if (legacyGetUserMedia) {
    return new Promise((resolve, reject) => {
      legacyGetUserMedia.call(navigator, constraints, resolve, reject);
    });
  }
  
  throw new Error('浏览器不支持getUserMedia');
}

/**
 * 获取媒体设备列表（兼容性处理）
 */
export async function enumerateDevices() {
  if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
    return await navigator.mediaDevices.enumerateDevices();
  }
  
  // 旧版API
  if (MediaStreamTrack && MediaStreamTrack.getSources) {
    return new Promise((resolve, reject) => {
      MediaStreamTrack.getSources((sources) => {
        resolve(sources);
      });
    });
  }
  
  throw new Error('浏览器不支持enumerateDevices');
}

/**
 * 检查AudioWorklet是否可用
 */
export function isAudioWorkletSupported(audioContext) {
  if (!audioContext) return false;
  return !!(audioContext.audioWorklet && audioContext.audioWorklet.addModule);
}

/**
 * 加载AudioWorklet模块（兼容性处理）
 */
export async function loadAudioWorklet(audioContext, moduleUrl) {
  if (!isAudioWorkletSupported(audioContext)) {
    throw new Error('AudioWorklet不支持');
  }
  
  try {
    await audioContext.audioWorklet.addModule(moduleUrl);
    return true;
  } catch (error) {
    try {
      const urlWithTimestamp = `${moduleUrl}?t=${Date.now()}`;
      await audioContext.audioWorklet.addModule(urlWithTimestamp);
      return true;
    } catch (retryError) {
      throw new Error(`加载AudioWorklet失败: ${retryError.message}`);
    }
  }
}

/**
 * 创建音频处理节点（自动选择AudioWorklet或ScriptProcessor）
 */
export async function createAudioProcessor(audioContext, options = {}) {
  const {
    workletUrl = '/audio-processor.js',
    workletName = 'audio-processor',
    bufferSize = 4096,
    inputChannels = 1,
    outputChannels = 1,
    onAudioData = null
  } = options;
  
  // 尝试使用AudioWorklet
  if (isAudioWorkletSupported(audioContext)) {
    try {
      await loadAudioWorklet(audioContext, workletUrl);
      const workletNode = new AudioWorkletNode(audioContext, workletName);
      
      if (onAudioData) {
        workletNode.port.onmessage = (event) => {
          if (event.data && event.data.audioData) {
            onAudioData(event.data.audioData);
          }
        };
      }
      
      return {
        node: workletNode,
        type: 'worklet',
        disconnect: () => {
          try {
            workletNode.disconnect();
          } catch (error) {
          }
        }
      };
    } catch (error) {
    }
  }
  
  // 回退到ScriptProcessor
  if (audioContext.createScriptProcessor) {
    const processor = audioContext.createScriptProcessor(
      bufferSize,
      inputChannels,
      outputChannels
    );
    
    if (onAudioData) {
      processor.onaudioprocess = (event) => {
        const inputBuffer = event.inputBuffer;
        const inputData = inputBuffer.getChannelData(0);
        onAudioData(inputData);
      };
    }
    
    return {
      node: processor,
      type: 'scriptprocessor',
      disconnect: () => {
        try {
          processor.disconnect();
          processor.onaudioprocess = null;
        } catch (error) {
        }
      }
    };
  }
  
  throw new Error('浏览器不支持音频处理节点');
}

/**
 * 创建WebSocket连接（兼容性处理）
 */
export function createWebSocket(url) {
  if (!window.WebSocket) {
    throw new Error('浏览器不支持WebSocket');
  }
  
  try {
    return new WebSocket(url);
  } catch (error) {
    throw new Error(`创建WebSocket失败: ${error.message}`);
  }
}

/**
 * 获取SpeechRecognition构造函数（兼容性处理）
 */
export function getSpeechRecognition() {
  return window.SpeechRecognition || window.webkitSpeechRecognition;
}

/**
 * 创建SpeechRecognition实例（兼容性处理）
 */
export function createSpeechRecognition() {
  const SpeechRecognitionClass = getSpeechRecognition();
  if (!SpeechRecognitionClass) {
    throw new Error('浏览器不支持SpeechRecognition');
  }
  
  return new SpeechRecognitionClass();
}

/**
 * 检查是否需要用户交互来启动音频（某些浏览器的策略）
 */
export function requiresUserInteraction() {
  const { browserName } = detectBrowser();
  // Safari和某些移动浏览器需要用户交互
  return browserName === 'Safari' || /iPad|iPhone|iPod/.test(navigator.userAgent);
}

/**
 * 生成浏览器兼容性报告
 */
export function generateCompatibilityReport() {
  const browser = detectBrowser();
  const support = checkAudioAPISupport();
  
  const report = {
    browser,
    support,
    compatible: true,
    warnings: [],
    errors: []
  };
  
  // Firefox浏览器不支持，标记为不兼容
  if (browser.browserName === 'Firefox') {
    report.compatible = false;
    report.errors.push('Firefox浏览器暂不支持本平台的核心功能');
    return report;
  }
  
  // 检查关键功能
  if (!support.audioContext) {
    report.compatible = false;
    report.errors.push('不支持AudioContext');
  }
  
  if (!support.getUserMedia) {
    report.compatible = false;
    report.errors.push('不支持getUserMedia');
  }
  
  if (!support.webSocket) {
    report.compatible = false;
    report.errors.push('不支持WebSocket');
  }
  
  // 检查可选功能
  if (!support.audioWorklet) {
    report.warnings.push('不支持AudioWorklet，将使用ScriptProcessor（已废弃但兼容）');
  }
  
  if (!support.speechRecognition) {
    report.warnings.push('不支持浏览器内置语音识别，将使用服务器端识别');
  }
  
  return report;
}

/**
 * 打印兼容性报告到控制台
 */
export function logCompatibilityReport() {
  const report = generateCompatibilityReport();
  
  
  // if (report.warnings.length > 0) {
  //   console.warn('⚠️ 警告:', report.warnings);
  // }
  
  // if (report.errors.length > 0) {
  //   console.error('❌ 错误:', report.errors);
  // }
  
  console.groupEnd();
  
  return report;
}

/**
 * 显示浏览器兼容性提示（如果需要）
 */
export function showCompatibilityWarning() {
  const report = generateCompatibilityReport();
  
  if (!report.compatible) {
    const message = `您的浏览器 ${report.browser.browserName} ${report.browser.version} 不完全支持本应用所需的功能。\n\n问题:\n${report.errors.join('\n')}\n\n建议使用最新版本的 Chrome、Edge、Firefox 或 360极速浏览器。`;
    
    alert(message);
    return false;
  }
  
  return true;
}

const browserCompatibility = {
  detectBrowser,
  checkAudioAPISupport,
  getAudioContext,
  createAudioContext,
  resumeAudioContext,
  getUserMedia,
  enumerateDevices,
  isAudioWorkletSupported,
  loadAudioWorklet,
  createAudioProcessor,
  createWebSocket,
  getSpeechRecognition,
  createSpeechRecognition,
  requiresUserInteraction,
  generateCompatibilityReport,
  logCompatibilityReport,
  showCompatibilityWarning
};

export default browserCompatibility;
